import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { PublicLayout } from '@/components/public';
import { toast } from 'sonner';
import { API } from '../../constants';
import { t } from '@/i18n';
import { 
  Phone, Mail, MapPin, Clock, Send, MessageSquare, 
  ArrowRight, CheckCircle, Building2
} from 'lucide-react';

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Send contact form to backend
      await axios.post(`${API}/contact`, formData);
      
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: t("Phone"),
      value: '561-680-2857',
      link: 'tel:561-680-2857',
      description: t("Mon-Fri, 8am-6pm EST"),
    },
    {
      icon: Mail,
      title: t("Email"),
      value: 'info@allamericannotice.com',
      link: 'mailto:info@allamericannotice.com',
      description: t("We respond within 24 hours"),
    },
    {
      icon: MapPin,
      title: t("Address"),
      value: '1732 S Congress Ave Suite 107',
      link: null,
      description: t("Palm Springs, FL 33461"),
    },
    {
      icon: Clock,
      title: t("Business Hours"),
      value: 'Mon - Fri: 8am - 6pm EST',
      link: null,
      description: t("Weekend support available"),
    },
  ];

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-[#1e3a5f] via-[#2d4a6f] to-[#1e3a5f]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-[#F97316] text-white mb-4">{t("Get In Touch")}</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
            {t("Contact Us")}
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Have questions? Need help with your lien documents? Our team is here to assist you. 
            Reach out and we&apos;ll get back to you promptly.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-md transition-shadow text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-[#1e3a5f]/10 flex items-center justify-center mx-auto mb-4">
                    <info.icon className="h-6 w-6 text-[#F97316]" />
                  </div>
                  <h3 className="font-semibold text-[#1e3a5f] mb-1">{info.title}</h3>
                  {info.link ? (
                    <a 
                      href={info.link} 
                      className="text-[#F97316] hover:underline font-medium"
                    >
                      {info.value}
                    </a>
                  ) : (
                    <p className="text-slate-700 font-medium">{info.value}</p>
                  )}
                  <p className="text-slate-500 text-sm mt-1">{info.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 lg:py-20 bg-slate-50" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <Card className="border-slate-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl text-[#1e3a5f] flex items-center gap-3">
                    <MessageSquare className="h-6 w-6 text-[#F97316]" />
                    {t("Send Us a Message")}
                  </CardTitle>
                  <CardDescription>
                    {t("Fill out the form below and we'll get back to you within 24 hours.")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">{t("Full Name *")}</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder={t("John Smith")}
                          required
                          data-testid="contact-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">{t("Email Address *")}</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@company.com"
                          required
                          data-testid="contact-email"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">{t("Phone Number")}</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 123-4567"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company">{t("Company Name")}</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder={t("ABC Construction")}
                          data-testid="contact-company"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">{t("Subject *")}</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder={t("How can we help you?")}
                        required
                        data-testid="contact-subject"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">{t("Message *")}</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={t("Tell us more about your question or how we can assist you...")}
                        rows={5}
                        required
                        data-testid="contact-message"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-[#F97316] hover:bg-[#ea6a10] h-12 text-base"
                      disabled={isSubmitting}
                      data-testid="contact-submit"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          {t("Send Message")} <Send className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Info Side */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">{t("Why Contact Us?")}</h2>
                <ul className="space-y-3">
                  {[
                    t("Get answers to your lien law questions"),
                    t("Help with your notice or document"),
                    t("Request a quote for bulk orders"),
                    t("Technical support for your account"),
                    t("Partnership or integration inquiries"),
                    t("General feedback or suggestions"),
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3 text-slate-700">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="text-lg font-bold text-[#1e3a5f] mb-4">{t("Quick Response Guarantee")}</h3>
                <p className="text-slate-600 mb-4">
                  {t("We value your time. Our team is committed to responding to all inquiries within 24 business hours. For urgent matters, please call us directly.")}
                </p>
                <div className="flex items-center gap-4 p-4 bg-[#F97316]/10 rounded-lg">
                  <Phone className="h-8 w-8 text-[#F97316]" />
                  <div>
                    <p className="text-sm text-slate-600">{t("Call us now")}</p>
                    <a href="tel:561-680-2857" className="text-xl font-bold text-[#1e3a5f]">
                      561-680-2857
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-[#1e3a5f] rounded-xl p-6 text-white">
                <Building2 className="h-10 w-10 text-[#F97316] mb-4" />
                <h3 className="text-lg font-bold mb-2">{t("Ready to Get Started?")}</h3>
                <p className="text-slate-300 mb-4">
                  {t("Create your account and send your first notice with confidence. $35–$45 per notice plus postage. No monthly fees, no commitments.")}
                </p>
                <Button 
                  onClick={() => navigate('/register')}
                  className="bg-[#F97316] hover:bg-[#ea6a10] w-full"
                >
                  {t("Get Started")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-[#1e3a5f]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            {t("Protecting Your Lien Rights, Made Simple")}
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            {t("Join thousands of construction professionals who trust All American Notice.")}
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/register')} 
            className="bg-[#F97316] hover:bg-[#ea6a10] text-white text-lg px-10 h-14 font-semibold"
          >
            {t("Get Started")} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ContactPage;
